// =============================================================================
// Catalogue cart — lightweight client-side store backed by localStorage.
// Lets the sales girl add multiple products from the catalogue and jump
// straight into a new order with everything pre-filled.
// =============================================================================
import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'kalachand.cart.v1';

export interface CartItem {
  productId: string;
  sku: string;
  name: string;
  unitPrice: number;          // snapshot at the time of add (so price changes don't surprise)
  vatRate: number;
  qty: number;
  imageUrl?: string | null;
  categoryName?: string | null;
  // Made-to-order finishes (from the model) the sales girl picks on the order.
  colorOptions?: string[];
  sideColorOptions?: string[];
  fabricOptions?: string[];
  densityOptions?: string[];
  // Finish chosen on the product page (editable later on the order).
  selectedColor?: string | null;
  selectedSideColor?: string | null;
  selectedFabric?: string | null;
  selectedDensity?: string | null;
  // Drives pickup-vs-delivery gating + earliest delivery date on the order.
  deliveryRequired?: boolean;
  fulfillmentStrategy?: string;
  // Free gifts linked to this product's model (opt-in at order create). Carried so
  // the order cart can preview them. Either a fixed variant (productId) or a whole
  // model (giftModelId + sizeOptions) whose size the sales girl picks on the order.
  gifts?: {
    productId: string | null;
    giftModelId: string | null;
    sku: string | null;
    name: string;
    qty: number;
    available: number | null;
    sizeOptions:
      | { productId: string; sku: string; size: string | null; available: number | null }[]
      | null;
  }[];
  // Bespoke mattress size. When isCustom, `productId` is a synthetic id (so custom
  // lines never merge) and `matchedVariantId` is the real variant the price was
  // matched to; the order page re-derives + recomputes the price server-side.
  isCustom?: boolean;
  matchedVariantId?: string;
  customWidthCm?: number;
  customLengthCm?: number;
  matchedSize?: string;
}

let state: CartItem[] = load();
const listeners = new Set<() => void>();

function load(): CartItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
function persist(): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  listeners.forEach((l) => l());
}

export const cart = {
  list(): CartItem[] {
    return state;
  },
  totalUnits(): number {
    return state.reduce((s, l) => s + l.qty, 0);
  },
  add(item: Omit<CartItem, 'qty'> & { qty?: number }): void {
    const qty = item.qty ?? 1;
    const existing = state.find((l) => l.productId === item.productId);
    if (existing) {
      existing.qty += qty;
      state = [...state];
    } else {
      state = [...state, { ...item, qty }];
    }
    persist();
  },
  update(productId: string, patch: Partial<Pick<CartItem, 'qty' | 'unitPrice'>>): void {
    state = state.map((l) => (l.productId === productId ? { ...l, ...patch } : l));
    persist();
  },
  remove(productId: string): void {
    state = state.filter((l) => l.productId !== productId);
    persist();
  },
  clear(): void {
    state = [];
    persist();
  },
  subscribe(fn: () => void): () => void {
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  },
};

export function useCart(): { items: CartItem[]; totalUnits: number } {
  const items = useSyncExternalStore(
    (cb) => cart.subscribe(cb),
    () => state,
    () => state,
  );
  return { items, totalUnits: items.reduce((s, l) => s + l.qty, 0) };
}
